import { useTranslation } from "react-i18next";
import Modal, { SimpleModalControls } from "../../components/Modal";
import { useState } from "react";
import { Divider, Text, FormControl, FormLabel, HStack, IconButton, Input, InputGroup, InputRightElement, VStack } from "@chakra-ui/react";
import AddCompany from "./components/AddCompany";
import AddPlaceOfBusiness from "./components/AddPlaceOfBusiness";
import SearchProductInInventory from "../../components/Modal/SearchProductInInventory";
import { BiTrash } from "react-icons/bi";
import AdditionalDetailsOutForm from "./components/AdditionalDetailsOutForm";
import { toast } from "react-toastify";
import { getAxiosInstance } from "../../utils/axiosInstance";
import dayjs from "dayjs";
import SearchProductInInventoryAfterName from "../../components/Modal/SearchProductInInventoryAfterName";
import ScanProductSerialNumbers from "./components/ScanProductSerialNumbers";
import {NewProduct} from "./components/Product";
import SearchProductInInventoryAfterSKU from "../../components/Modal/SearchProductInInventoryAfterSKU";

interface OutBillForm extends SimpleModalControls { };

const OutBillForm = (props: OutBillForm) => {
    const { t } = useTranslation("translations");

    const [billNo, setBillNo] = useState<string>("");
    const [billDate, setBillDate] = useState<string>(dayjs(new Date()).format("YYYY-MM-DD"));
    const [company, setCompany] = useState<string>("");
    const [placeOfBusiness, setPlaceOfBusiness] = useState<string>("");
    const [additionalDetails, setAdditionalDetails] = useState<any>({});

    const [products, setProducts] = useState<any[]>([]);

    const deleteProduct = (index: number) => {
        const tmp = [...products];
        tmp.splice(index, 1);
        setProducts(tmp);
    };

    const onEditProduct = (index: number, newQuantity: number) => {
        const tmp = [...products];
        tmp[index].quantity = parseInt(Math.min(tmp[index].maxQuantity, newQuantity) + "");

        // update the size of the serial numbers array if the product has serial numbers
        if (tmp[index].hasSerialNumber && tmp[index].serialNumbers.length !== tmp[index].quantity) {
            const diff = tmp[index].quantity - tmp[index].serialNumbers.length;

            if(diff >= 1) {
                tmp[index].serialNumbers = [...tmp[index].serialNumbers, ...new Array(diff).fill("")];
            } else {
                tmp[index].serialNumbers = tmp[index].serialNumbers.splice(tmp[index].serialNumbers.length + diff, diff)
            }
        }

        setProducts(tmp);
    };

    const createBill = async () => {
        try {
            await getAxiosInstance().post("/Bill/Out",
                {
                    placeOfBusinessId: placeOfBusiness,
                    companyId: company,
                    billNo: billNo,
                    billDate: billDate,
                    products: products,
                    aditionalDetails: additionalDetails
                });
            toast.success(t("OUT_BILL_CREATE_SUCCESSFULLY"), { position: "bottom-right" });
            props.onClose();
        } catch (e) {
            toast.error(t("SOMETHING_WENT_WRONG"), { position: "bottom-right" });
        }
    }

    const validateSerialNumber = async (id: string, productId: string, serialNumber: string): Promise<boolean> => {
        try {
            const res = await getAxiosInstance().get(`Inventory/PlaceOfBusiness/${id}/${productId}/${serialNumber}`);
            return res.data;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    const onEditProductSerialNumbers = (arr: string[], index: number) => {
        const tmp = [...products];
        tmp[index].serialNumbers = arr;
        setProducts(tmp);
    }

    const addProduct = (data: NewProduct & { id: string; quantity: number }) => {
        setProducts([
            ...products,
            {
                ...data,
                quantity: 0,
                maxQuantity: data.quantity,
                serialNumbers: data.hasSerialNumber ? [] : undefined
            }
        ]);
    }

    return (
        <Modal
            header={(t("OUT_BILL_TITLE"))}
            isOpen={props.isOpen}
            onClose={props.onClose}
            secondaryActionName={t("CREATE_BILL")}
            secondaryActionEnabled={true}
            secondaryAction={() => createBill()}
            size={"2xl"}
        >
            <div>
                <HStack>
                    <FormControl>
                        <FormLabel>{t("BILL_NO")}</FormLabel>
                        <Input
                            isRequired
                            name='bill_no'
                            value={billNo}
                            onChange={(e) => setBillNo(e.target.value)}
                            placeholder={t("BILL_NO_INPUT_PLACEHOLDER")}
                        />
                    </FormControl>
                    <FormControl>
                        <FormLabel>{t("BILL_DATE")}</FormLabel>
                        <Input
                            isRequired
                            type="date"
                            name='bill_date'
                            value={billDate}
                            onChange={(e) => setBillDate(e.target.value)}
                            placeholder={t("BILL_DATE_INPUT_PLACEHOLDER")}
                        />
                    </FormControl>
                </HStack>
                <AddCompany
                    selectedCompany={company}
                    setSelectedCompany={setCompany}
                />
                <AddPlaceOfBusiness
                    companyId={company}
                    selectedPlaceOfBusiness={placeOfBusiness}
                    setSelectedPlaceOfBusiness={setPlaceOfBusiness}
                />

                <VStack align="start">
                    <Text fontWeight="semibold" className="mt-4" size="md">{t("BUYER_DETAILS")}</Text>
                    <Divider />
                </VStack>

                <VStack className="mt-4">
                    <AdditionalDetailsOutForm
                        data={additionalDetails}
                        onChange={(d) => setAdditionalDetails(d)}
                    />
                </VStack>

                <SearchProductInInventory
                    addProduct={(data) => addProduct(data)}
                    placeOfBusinessId={placeOfBusiness}
                />

                <SearchProductInInventoryAfterName
                    addProduct={(data) => addProduct(data)}
                    placeOfBusinessId={placeOfBusiness}
                />

                <SearchProductInInventoryAfterSKU
                    addProduct={(data) => addProduct(data)}
                    placeOfBusinessId={placeOfBusiness}
                />

                <VStack align="start">
                    <Text fontWeight="semibold" className="mt-4" size="md">{t("TRANSFER_PRODUCT")}</Text>
                    <Divider />
                </VStack>
                {
                    products.map((p, index) => <div>
                        <div className="font-bold text-xl my-2">
                            {p.productName} ({t("MAX_QUANTITY")}: {p.maxQuantity})
                        </div>
                        <div className="flex flex-row items-center">
                            <IconButton
                                colorScheme='red'
                                aria-label='Remove element'
                                fontSize={25}
                                icon={<BiTrash />}
                                mr={2}
                                onClick={() => deleteProduct(index)}
                            />
                            <InputGroup>
                                <Input
                                    placeholder={t("OUT_QUANTITY")}
                                    type="number"
                                    inputMode="numeric"
                                    value={p.quantity}
                                    onChange={(e) => onEditProduct(index, +e.target.value)}
                                />
                                <InputRightElement className="text-blue-500 mr-6">
                                    ({p.um})
                                </InputRightElement>
                            </InputGroup>
                        </div>
                        {
                            p.hasSerialNumber && p.serialNumbers &&
                            <ScanProductSerialNumbers
                              serialNumbers={p.serialNumbers}
                              onSerialNumbersChange={(arr: string[]) => onEditProductSerialNumbers(arr, index)}
                              validateSerialNumber={true}
                              validateFunction={async (serial) => await validateSerialNumber(placeOfBusiness, p.productId, serial)}
                            />
                        }
                    </div>)
                }
            </div>
        </Modal>
    )
}

export default OutBillForm;